import React, { useState } from 'react';
import { Box, Typography, Card, IconButton, Collapse } from '@mui/material';
import ChevronDownIcon from '@mui/icons-material/ExpandMore';
import ChevronUpIcon from '@mui/icons-material/ExpandLess';
import PaymentCard, { PaymentCardProps } from '../PaymentCard/PaymentCard';
import { IPaymentCard } from '../../types/global/generic.types';
import { SimpleAddCardModal } from '../Payment/SimpleAddCardModal';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { isEmpty } from '../../utils/lodash';

export interface PaymentCardDropdownProps {
    cards?: IPaymentCard[];
    paymentCardProps?: Omit<PaymentCardProps, 'card'>;
    selectedCard?: IPaymentCard;
    onCardSelect?: (card: IPaymentCard) => void;
}

const PaymentCardDropdown: React.FC<PaymentCardDropdownProps> = ({
    cards,
    selectedCard,
    onCardSelect,
    paymentCardProps,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { themeStyle } = useThemeContext();

    const handleToggle = () => {
        setIsExpanded(!isExpanded);
    };

    const handleCardSelect = (card: IPaymentCard) => {
        onCardSelect && onCardSelect(card);
        setIsExpanded(false);
    };

    return (
        <Box p={3} sx={{ width: '100%', boxSizing: 'border-box' }}>
            <Card
                sx={{
                    width: '100%',
                    padding: 2,
                    borderRadius: 2,
                    backgroundColor: theme[themeStyle].deep_grey_lighter,
                    boxShadow: isExpanded ? '0 4px 10px rgba(0,0,0,0.1)' : undefined,
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                }}>
                {/* Collapsed Header */}
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    onClick={handleToggle}
                    sx={{
                        cursor: 'pointer',
                        width: '100%',
                    }}>
                    <Box display="flex" alignItems="center" sx={{ flexGrow: 1, overflow: 'hidden' }}>
                        {selectedCard ? (
                            <PaymentCard card={selectedCard} isModalMode cardSize="30px" isSelected />
                        ) : (
                            <Typography
                                style={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].tag_white,
                                    fontSize: 12,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}>
                                {isEmpty(cards) ? 'No card on file. Add new card' : 'Select a card'}
                            </Typography>
                        )}
                    </Box>
                    <IconButton sx={{ flexShrink: 0 }}>
                        {isExpanded ? (
                            <ChevronUpIcon sx={{ color: theme[themeStyle].highlight }} />
                        ) : (
                            <ChevronDownIcon sx={{ color: theme[themeStyle].highlight }} />
                        )}
                    </IconButton>
                </Box>

                {/* Animated Expansion */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box mt={2} sx={{ width: '100%' }}>
                        {cards &&
                            cards.map((card, index) => (
                                <Box
                                    key={index}
                                    onClick={() => handleCardSelect(card)}
                                    sx={{
                                        cursor: 'pointer',
                                        marginBottom: 1,
                                        width: '100%',
                                        overflow: 'hidden',
                                        '&:hover': {
                                            backgroundColor: theme[themeStyle].deep_grey_lighter,
                                            borderRadius: 2,
                                        },
                                    }}>
                                    <PaymentCard
                                        card={card}
                                        isSelected={selectedCard?._id === card._id}
                                        isModalMode
                                        cardSize="30px"
                                        {...paymentCardProps}
                                    />
                                </Box>
                            ))}
                    </Box>
                </Collapse>
            </Card>
            <SimpleAddCardModal />
        </Box>
    );
};

export { PaymentCardDropdown };
